import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  alterarPessoaParametroSchema,
  criarPessoaParametroSchema,
  excluirPessoaParametroSchema,
  type ApropriacaoEntrada,
  type PessoaParametroResponse,
  type PessoaResponse,
} from '@triade/contracts';
import { useCargos } from '@/api/cargos';
import { useMe } from '@/api/me';
import { useAlterarParametroPessoa, useCriarParametroPessoa, useExcluirParametroPessoa } from '@/api/pessoas';
import { ApropriacaoPorCentro, type ErroApropriacao } from '@/components/apropriacao';
import { FornecedorCombobox, type FornecedorResumo } from '@/components/fornecedores';
import { MensagemCampo } from '@/components/item-cadastro/dados-item';
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ApiError } from '@/lib/api';
import { decimalDigitado, decimalParaTextoCampo, formatarData, formatarFracaoPercentual, formatarMoeda, formatarQuantidade } from '@/lib/format';
import { podeCorrigirVigencia } from './vigencia-permissoes';

const CAMPO = 'h-[34px] w-full rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-70';
const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';
const TRAVADA = 'Usada por um rateio fechado';

const mensagemApi = (e: unknown) => (e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API');

/** Dica em portugues para os codigos de vigencia da API (a mensagem da API vai antes, com o code). */
const DICAS_VIGENCIA: Record<string, string> = {
  VIGENCIA_NO_MEIO: 'Esta vigência não é a última: exclua primeiro as mais recentes.',
  VIGENCIA_EM_RATEIO_FECHADO: 'Usada por um rateio fechado: não pode ser alterada nem excluída.',
  VIGENCIA_ANTERIOR_AO_INICIO: 'O início é anterior ao da vigência anterior. Ajuste a data.',
  VIGENCIA_INICIO_DEPOIS_DO_FIM: 'O início é posterior ao fim desta vigência. Ajuste a data.',
};
const erroVigencia = (e: unknown): string => {
  const dica = e instanceof ApiError && e.code ? DICAS_VIGENCIA[e.code] : undefined;
  return dica ? `${mensagemApi(e)}. ${dica}` : mensagemApi(e);
};

/** Campos da vigencia (texto de tela). Decimais viram string com ponto so no envio (decimalDigitado). */
const vigenciaCampos = z.object({
  validFrom: z.string().refine((v) => z.iso.date().safeParse(v).success, 'Informe o início da vigência'),
  cargoId: z.string().min(1, 'Escolha o cargo'),
  salario: z.string().min(1, 'Informe o salário'),
  gratificacoes: z.string(),
  bonus: z.string(),
  horasDia: z.string().min(1, 'Informe as horas por dia'),
  diasMes: z.string().min(1, 'Informe os dias por mês'),
  terceirizado: z.boolean(),
  fornecedorId: z.string(),
});
const exigeFornecedor = (v: { terceirizado: boolean; fornecedorId: string }, ctx: z.RefinementCtx) => {
  if (v.terceirizado && !v.fornecedorId) ctx.addIssue({ code: 'custom', path: ['fornecedorId'], message: 'Escolha o fornecedor (terceirizado)' });
};
/** Nova vigencia: motivo opcional. */
const vigenciaFormSchema = vigenciaCampos.extend({ motivo: z.string().trim().max(300, 'O motivo tem no máximo 300 caracteres') }).superRefine(exigeFornecedor);
/** Correcao: motivo obrigatorio (3 a 300). */
const correcaoFormSchema = vigenciaCampos
  .extend({ motivo: z.string().trim().min(3, 'Informe o motivo (mínimo 3 caracteres)').max(300, 'O motivo tem no máximo 300 caracteres') })
  .superRefine(exigeFornecedor);
type VigenciaForm = z.input<typeof vigenciaFormSchema>;
type CorrecaoForm = z.input<typeof correcaoFormSchema>;

const CAMPOS_DECIMAIS = ['salario', 'gratificacoes', 'bonus', 'horasDia', 'diasMes'] as const;

function hojeIso(): string {
  const d = new Date();
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
}

/** Aba "Parametros de trabalho": apropriacao atual (leitura), historico das vigencias (valores da API), correcao/exclusao por papel e o formulario da nova vigencia. */
export function ParametrosPessoa({ pessoa, podeEditar }: { pessoa: PessoaResponse; podeEditar: boolean }) {
  const historico = pessoa.parametros ?? (pessoa.parametroVigente ? [pessoa.parametroVigente] : []);
  const papeis = useMe().data?.papeis;
  const podeCorrigir = podeEditar && podeCorrigirVigencia(papeis);
  return (
    <div className="flex flex-col gap-6">
      <ApropriacaoAtual apropriacao={pessoa.parametroVigente?.apropriacao ?? null} />
      <HistoricoVigencias pessoaId={pessoa.id} historico={historico} podeCorrigir={podeCorrigir} />
      {podeEditar ? <NovaVigencia pessoa={pessoa} /> : <p className="text-small text-ink-muted">Sem permissão para registrar nova vigência.</p>}
    </div>
  );
}

/** Apropriacao da vigencia atual, so leitura (o componente novo, modo leitura: valores da API). */
function ApropriacaoAtual({ apropriacao }: { apropriacao: PessoaParametroResponse['apropriacao'] }) {
  return (
    <section aria-label="Apropriação atual" data-testid="apropriacao-vigente" className="rounded-2xl border border-border p-4">
      <h3 className="mb-3 text-body font-semibold">Apropriação atual</h3>
      {apropriacao ? (
        <ApropriacaoPorCentro modo="leitura" resolvida={apropriacao} referTo="PESSOA" />
      ) : (
        <p className="text-small text-ink-muted">Sem apropriação na vigência atual.</p>
      )}
    </section>
  );
}

function HistoricoVigencias({ pessoaId, historico, podeCorrigir }: { pessoaId: string; historico: PessoaParametroResponse[]; podeCorrigir: boolean }) {
  const [corrigindo, setCorrigindo] = useState<PessoaParametroResponse | null>(null);
  const [excluindo, setExcluindo] = useState<PessoaParametroResponse | null>(null);
  if (historico.length === 0) return <p className="text-small text-ink-muted">Nenhuma vigência de parâmetros cadastrada.</p>;
  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full min-w-310 border-collapse text-small" aria-label="Histórico de vigências">
          <thead>
            <tr className="text-left text-caption text-ink-muted">
              <th scope="col" className="px-3 py-2 font-medium">Vigência</th>
              <th scope="col" className="px-3 py-2 font-medium">Cargo</th>
              <th scope="col" className="px-3 py-2 font-medium">Salário</th>
              <th scope="col" className="px-3 py-2 font-medium">Gratificações e adicionais</th>
              <th scope="col" className="px-3 py-2 font-medium">Bônus e benefícios</th>
              <th scope="col" className="px-3 py-2 font-medium">Salário total</th>
              <th scope="col" className="px-3 py-2 font-medium">Horas/dia</th>
              <th scope="col" className="px-3 py-2 font-medium">Dias/mês</th>
              <th scope="col" className="px-3 py-2 font-medium">Terceirizado</th>
              <th scope="col" className="px-3 py-2 font-medium">Custo mensal</th>
              <th scope="col" className="px-3 py-2 font-medium">Encargos</th>
              <th scope="col" className="px-3 py-2 font-medium">Gravado por / quando</th>
              {podeCorrigir && <th scope="col" className="px-3 py-2 font-medium">Ações</th>}
            </tr>
          </thead>
          <tbody>
            {historico.map((p) => (
              <tr key={p.id} data-testid="linha-vigencia" className="border-t border-line-soft">
                <td className="tabular px-3 py-2.5">
                  {formatarData(p.validFrom)} — {p.validTo ? formatarData(p.validTo) : 'vigente'}
                </td>
                <td className="px-3 py-2.5">{p.cargo.nome}</td>
                <td className="tabular px-3 py-2.5">{formatarMoeda(p.salario)}</td>
                <td className="tabular px-3 py-2.5">{formatarMoeda(p.gratificacoes)}</td>
                <td className="tabular px-3 py-2.5">{formatarMoeda(p.bonus)}</td>
                <td className="tabular px-3 py-2.5">{formatarMoeda(p.salarioTotal)}</td>
                <td className="tabular px-3 py-2.5">{formatarQuantidade(p.horasDia)}</td>
                <td className="tabular px-3 py-2.5">{formatarQuantidade(p.diasMes)}</td>
                <td className="px-3 py-2.5">{p.terceirizado ? `Sim${p.fornecedor ? ` (${p.fornecedor.nome})` : ''}` : 'Não'}</td>
                <td className="tabular px-3 py-2.5">{p.custoMensal ? formatarMoeda(p.custoMensal) : '—'}</td>
                <td className="tabular px-3 py-2.5">{p.encargosSociais ? formatarFracaoPercentual(p.encargosSociais) : '—'}</td>
                <td className="px-3 py-2.5">
                  <span className="block">{p.criadoPor?.nome ?? '—'}</span>
                  <span className="block text-caption text-ink-muted">{formatarData(p.criadoEm)}</span>
                </td>
                {podeCorrigir && (
                  <td className="px-3 py-2.5">
                    <div className="flex gap-1">
                      <Button type="button" variant="ghost" size="sm" disabled={p.travada} title={p.travada ? TRAVADA : undefined} onClick={() => setCorrigindo(p)}>
                        Editar
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-danger-ink"
                        disabled={p.travada}
                        title={p.travada ? TRAVADA : undefined}
                        onClick={() => setExcluindo(p)}
                      >
                        Excluir
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {corrigindo && <CorrigirVigenciaDialog pessoaId={pessoaId} parametro={corrigindo} onFechar={() => setCorrigindo(null)} />}
      {excluindo && <ExcluirVigenciaDialog pessoaId={pessoaId} parametro={excluindo} onFechar={() => setExcluindo(null)} />}
    </>
  );
}

/** Corrige uma vigencia gravada. Envia so o que mudou (+ motivo). A apropriacao nao e editada aqui. */
function CorrigirVigenciaDialog({ pessoaId, parametro, onFechar }: { pessoaId: string; parametro: PessoaParametroResponse; onFechar: () => void }) {
  const cargos = useCargos({ ativo: true, limit: 200 });
  const alterar = useAlterarParametroPessoa();
  const [erro, setErro] = useState<string | null>(null);
  const [fornecedor, setFornecedor] = useState<FornecedorResumo | null>(parametro.fornecedor);
  /** Valores de partida (como a API devolveu, em texto de campo). Comparados para saber o que mudou. */
  const inicial = useMemo<CorrecaoForm>(
    () => ({
      validFrom: parametro.validFrom.slice(0, 10),
      cargoId: parametro.cargo.id,
      salario: decimalParaTextoCampo(parametro.salario),
      gratificacoes: decimalParaTextoCampo(parametro.gratificacoes),
      bonus: decimalParaTextoCampo(parametro.bonus),
      horasDia: decimalParaTextoCampo(parametro.horasDia),
      diasMes: decimalParaTextoCampo(parametro.diasMes),
      terceirizado: parametro.terceirizado,
      fornecedorId: parametro.fornecedor?.id ?? '',
      motivo: '',
    }),
    [parametro],
  );
  const form = useForm<CorrecaoForm, unknown, z.output<typeof correcaoFormSchema>>({ resolver: zodResolver(correcaoFormSchema), defaultValues: inicial, mode: 'onTouched' });
  const { errors } = form.formState;
  const terceirizado = form.watch('terceirizado');
  const salvando = alterar.isPending;
  const opcoesCargo = cargos.data ?? [];
  /** Cargo da vigencia pode estar inativo (fora da lista de ativos): mantem a opcao para o select mostrar o valor gravado. */
  const cargoForaDaLista = !opcoesCargo.some((c) => c.id === parametro.cargo.id);

  async function gravar(v: z.output<typeof correcaoFormSchema>) {
    setErro(null);
    const digitados: Record<(typeof CAMPOS_DECIMAIS)[number], string> = {
      salario: v.salario,
      gratificacoes: v.gratificacoes.trim() || '0',
      bonus: v.bonus.trim() || '0',
      horasDia: v.horasDia,
      diasMes: v.diasMes,
    };
    const alteracoes: Record<string, unknown> = {};
    if (v.validFrom !== inicial.validFrom) alteracoes.validFrom = v.validFrom;
    if (v.cargoId !== inicial.cargoId) alteracoes.cargoId = v.cargoId;
    for (const campo of CAMPOS_DECIMAIS) {
      if (digitados[campo] === inicial[campo]) continue;
      const decimal = decimalDigitado(digitados[campo]);
      if (decimal === null) {
        form.setError(campo, { message: 'Informe um valor decimal (ex.: 2800 ou 2800,50)' });
        return;
      }
      alteracoes[campo] = decimal;
    }
    if (v.terceirizado !== inicial.terceirizado) alteracoes.terceirizado = v.terceirizado;
    const fornecedorNovo = v.terceirizado ? v.fornecedorId : null;
    if (fornecedorNovo !== (inicial.fornecedorId || null)) alteracoes.fornecedorId = fornecedorNovo;

    const dto = alterarPessoaParametroSchema.safeParse({ ...alteracoes, motivo: v.motivo.trim() });
    if (!dto.success) {
      setErro(dto.error.issues[0]?.message ?? 'Dados inválidos');
      return;
    }
    try {
      await alterar.mutateAsync({ pessoaId, parametroId: parametro.id, ...dto.data });
      onFechar();
    } catch (e) {
      setErro(erroVigencia(e));
    }
  }

  return (
    <Dialog open onOpenChange={(aberto) => !aberto && onFechar()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Corrigir vigência</DialogTitle>
          <DialogDescription>
            Altera só os campos que você mudar, com motivo. A apropriação não é editada aqui: para mudar a apropriação, use Nova vigência.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(gravar, () => setErro('Corrija os campos destacados'))} noValidate>
          <fieldset disabled={salvando} className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-4">
            <div>
              <label htmlFor="cor-inicio" className={ROTULO}>Início da vigência</label>
              <input id="cor-inicio" type="date" className={CAMPO} aria-invalid={errors.validFrom ? true : undefined} {...form.register('validFrom')} />
              <MensagemCampo id="cor-inicio-erro" mensagem={errors.validFrom?.message} />
            </div>
            <div>
              <label htmlFor="cor-cargo" className={ROTULO}>Cargo</label>
              <select id="cor-cargo" className={CAMPO} aria-invalid={errors.cargoId ? true : undefined} {...form.register('cargoId')}>
                <option value="">Escolha o cargo</option>
                {cargoForaDaLista && <option value={parametro.cargo.id}>{parametro.cargo.nome}</option>}
                {opcoesCargo.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>
              <MensagemCampo id="cor-cargo-erro" mensagem={errors.cargoId?.message} />
            </div>
            <div>
              <label htmlFor="cor-salario" className={ROTULO}>Salário (R$)</label>
              <input id="cor-salario" inputMode="decimal" className={`${CAMPO} tabular`} aria-invalid={errors.salario ? true : undefined} {...form.register('salario')} />
              <MensagemCampo id="cor-salario-erro" mensagem={errors.salario?.message} />
            </div>
            <div>
              <label htmlFor="cor-gratificacoes" className={ROTULO}>Gratificações e adicionais (R$)</label>
              <input id="cor-gratificacoes" inputMode="decimal" className={`${CAMPO} tabular`} aria-invalid={errors.gratificacoes ? true : undefined} {...form.register('gratificacoes')} />
              <p className="mt-1 text-caption text-ink-muted">Comissão, gratificação, periculosidade, insalubridade: entram nos encargos</p>
              <MensagemCampo id="cor-gratificacoes-erro" mensagem={errors.gratificacoes?.message} />
            </div>
            <div>
              <label htmlFor="cor-bonus" className={ROTULO}>Bônus e benefícios (R$)</label>
              <input id="cor-bonus" inputMode="decimal" className={`${CAMPO} tabular`} aria-invalid={errors.bonus ? true : undefined} {...form.register('bonus')} />
              <p className="mt-1 text-caption text-ink-muted">VR, saúde e outros benefícios: sem encargos</p>
              <MensagemCampo id="cor-bonus-erro" mensagem={errors.bonus?.message} />
            </div>
            <div>
              <label htmlFor="cor-horas" className={ROTULO}>Horas por dia</label>
              <input id="cor-horas" inputMode="decimal" className={`${CAMPO} tabular`} aria-invalid={errors.horasDia ? true : undefined} {...form.register('horasDia')} />
              <MensagemCampo id="cor-horas-erro" mensagem={errors.horasDia?.message} />
            </div>
            <div>
              <label htmlFor="cor-dias" className={ROTULO}>Dias por mês</label>
              <input id="cor-dias" inputMode="decimal" className={`${CAMPO} tabular`} aria-invalid={errors.diasMes ? true : undefined} {...form.register('diasMes')} />
              <MensagemCampo id="cor-dias-erro" mensagem={errors.diasMes?.message} />
            </div>
            <label htmlFor="cor-terceirizado" className="flex cursor-pointer items-center gap-2 self-end">
              <input id="cor-terceirizado" type="checkbox" className="size-[18px] accent-primary" {...form.register('terceirizado')} />
              Terceirizado
            </label>
            {terceirizado && (
              <div>
                <span className={ROTULO}>Fornecedor</span>
                <Controller
                  control={form.control}
                  name="fornecedorId"
                  render={({ field }) => (
                    <FornecedorCombobox
                      valor={fornecedor}
                      podeCadastrar={false}
                      ariaLabel="Fornecedor da correção"
                      onSelecionar={(f) => {
                        setFornecedor(f);
                        field.onChange(f?.id ?? '');
                      }}
                    />
                  )}
                />
                <MensagemCampo id="cor-forn-erro" mensagem={errors.fornecedorId?.message} />
              </div>
            )}
            <div className="sm:col-span-2">
              <label htmlFor="cor-motivo" className={ROTULO}>Motivo da correção</label>
              <input id="cor-motivo" maxLength={300} className={CAMPO} aria-invalid={errors.motivo ? true : undefined} {...form.register('motivo')} />
              <MensagemCampo id="cor-motivo-erro" mensagem={errors.motivo?.message} />
            </div>
          </fieldset>
          {erro && (
            <p role="alert" className="mt-4 text-caption text-danger-ink">
              {erro}
            </p>
          )}
          <DialogFooter className="mt-5">
            <Button type="button" variant="outline" disabled={salvando} onClick={onFechar}>
              Cancelar
            </Button>
            <Button type="submit" disabled={salvando}>
              {salvando ? 'Salvando…' : 'Salvar correção'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Exclui uma vigencia, com motivo. A API decide (ultima reabre a anterior; do meio e rateio fechado sao recusados). */
function ExcluirVigenciaDialog({ pessoaId, parametro, onFechar }: { pessoaId: string; parametro: PessoaParametroResponse; onFechar: () => void }) {
  const excluir = useExcluirParametroPessoa();
  const [erro, setErro] = useState<string | null>(null);
  const form = useForm<z.input<typeof excluirPessoaParametroSchema>, unknown, z.output<typeof excluirPessoaParametroSchema>>({
    resolver: zodResolver(excluirPessoaParametroSchema),
    defaultValues: { motivo: '' },
    mode: 'onTouched',
  });
  const { errors } = form.formState;
  const salvando = excluir.isPending;

  async function gravar(v: z.output<typeof excluirPessoaParametroSchema>) {
    setErro(null);
    try {
      await excluir.mutateAsync({ pessoaId, parametroId: parametro.id, motivo: v.motivo });
      onFechar();
    } catch (e) {
      setErro(erroVigencia(e));
    }
  }

  return (
    <AlertDialog open onOpenChange={(aberto) => !aberto && onFechar()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Excluir vigência</AlertDialogTitle>
          <AlertDialogDescription>{`Excluir a vigência de ${formatarData(parametro.validFrom)}? Se for a última, a anterior volta a ficar vigente.`}</AlertDialogDescription>
        </AlertDialogHeader>
        <form onSubmit={form.handleSubmit(gravar)} noValidate className="flex flex-col gap-4">
          <div>
            <label htmlFor="exc-motivo" className={ROTULO}>Motivo da exclusão</label>
            <input id="exc-motivo" maxLength={300} className={CAMPO} aria-invalid={errors.motivo ? true : undefined} {...form.register('motivo')} />
            <MensagemCampo id="exc-motivo-erro" mensagem={errors.motivo?.message} />
          </div>
          {erro && (
            <p role="alert" className="text-caption text-danger-ink">
              {erro}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={salvando}>Cancelar</AlertDialogCancel>
            <Button type="submit" variant="destructive" disabled={salvando}>
              {salvando ? 'Excluindo…' : 'Excluir vigência'}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function NovaVigencia({ pessoa }: { pessoa: PessoaResponse }) {
  const cargos = useCargos({ ativo: true, limit: 200 });
  const criar = useCriarParametroPessoa();
  /** Apropriacao editada na tela (null = nada editado). `completa`: MANUAL fecha 100% e linhas completas. */
  const [apropriacao, setApropriacao] = useState<{ valor: ApropriacaoEntrada | null; completa: boolean } | null>(null);
  const [chaveApropriacao, setChaveApropriacao] = useState(0);
  const [erroApropriacao, setErroApropriacao] = useState<ErroApropriacao | null>(null);
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);
  const [salvoEm, setSalvoEm] = useState<string | null>(null);
  const [fornecedor, setFornecedor] = useState<FornecedorResumo | null>(null);
  const base = useMemo<VigenciaForm>(
    () => ({ validFrom: hojeIso(), cargoId: '', salario: '', gratificacoes: '0', bonus: '0', horasDia: '', diasMes: '', terceirizado: false, fornecedorId: '', motivo: '' }),
    [],
  );
  const form = useForm<VigenciaForm, unknown, z.output<typeof vigenciaFormSchema>>({ resolver: zodResolver(vigenciaFormSchema), defaultValues: base, mode: 'onTouched' });
  const { errors } = form.formState;
  const terceirizado = form.watch('terceirizado');
  const salvando = criar.isPending;

  async function gravar(v: z.output<typeof vigenciaFormSchema>) {
    setErroSalvar(null);
    setErroApropriacao(null);
    if (apropriacao && !apropriacao.completa) {
      setErroApropriacao({ code: 'APROPRIACAO_INCOMPLETA', message: 'A apropriação precisa fechar 100% (e ter todas as linhas completas) para salvar.' });
      return;
    }
    const numeros = {
      salario: decimalDigitado(v.salario),
      gratificacoes: decimalDigitado(v.gratificacoes || '0'),
      bonus: decimalDigitado(v.bonus || '0'),
      horasDia: decimalDigitado(v.horasDia),
      diasMes: decimalDigitado(v.diasMes),
    };
    for (const [campo, valor] of Object.entries(numeros)) {
      if (valor === null) {
        form.setError(campo as keyof VigenciaForm, { message: 'Informe um valor decimal (ex.: 2800 ou 2800,50)' });
        return;
      }
    }
    const entrada = {
      validFrom: v.validFrom,
      cargoId: v.cargoId,
      salario: numeros.salario,
      gratificacoes: numeros.gratificacoes,
      bonus: numeros.bonus,
      horasDia: numeros.horasDia,
      diasMes: numeros.diasMes,
      terceirizado: v.terceirizado,
      ...(v.terceirizado && v.fornecedorId ? { fornecedorId: v.fornecedorId } : {}),
      ...(apropriacao?.valor ? { apropriacao: apropriacao.valor } : {}),
      ...(v.motivo.trim() ? { motivo: v.motivo.trim() } : {}),
    };
    const dto = criarPessoaParametroSchema.safeParse(entrada);
    if (!dto.success) {
      setErroSalvar(dto.error.issues[0]?.message ?? 'Dados inválidos');
      return;
    }
    try {
      await criar.mutateAsync({ pessoaId: pessoa.id, ...dto.data });
      setSalvoEm(new Date().toLocaleTimeString('pt-BR'));
      setApropriacao(null);
      setFornecedor(null);
      setChaveApropriacao((k) => k + 1);
      form.reset({ ...base, validFrom: v.validFrom });
    } catch (e) {
      setErroSalvar(mensagemApi(e));
      if (e instanceof ApiError) {
        const issueApropriacao = e.body?.issues?.find((i) => i.path.startsWith('apropriacao'));
        if (issueApropriacao || e.code?.includes('SOMA') || e.code?.startsWith('APROPRIACAO')) {
          setErroApropriacao({ code: e.code ?? 'APROPRIACAO', message: issueApropriacao?.message ?? e.message });
        }
      }
    }
  }

  return (
    <section aria-label="Nova vigência" className="rounded-2xl border border-border p-4">
      <h3 className="text-body font-semibold">Nova vigência</h3>
      <p className="mt-1 text-caption text-ink-muted">A nova vigência fecha a anterior (nunca é retroativa). Custo mensal e salário total são calculados pela API depois de salvar.</p>
      <fieldset disabled={salvando} className="mt-4 grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-4">
        <div>
          <label htmlFor="vig-inicio" className={ROTULO}>Início da vigência</label>
          <input id="vig-inicio" type="date" className={CAMPO} aria-invalid={errors.validFrom ? true : undefined} {...form.register('validFrom')} />
          <MensagemCampo id="vig-inicio-erro" mensagem={errors.validFrom?.message} />
        </div>
        <div>
          <label htmlFor="vig-cargo" className={ROTULO}>Cargo</label>
          <select id="vig-cargo" className={CAMPO} aria-invalid={errors.cargoId ? true : undefined} {...form.register('cargoId')}>
            <option value="">Escolha o cargo</option>
            {(cargos.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
              </option>
            ))}
          </select>
          <MensagemCampo id="vig-cargo-erro" mensagem={errors.cargoId?.message} />
        </div>
        <div>
          <label htmlFor="vig-salario" className={ROTULO}>Salário (R$)</label>
          <input id="vig-salario" inputMode="decimal" className={`${CAMPO} tabular`} aria-invalid={errors.salario ? true : undefined} {...form.register('salario')} />
          <MensagemCampo id="vig-salario-erro" mensagem={errors.salario?.message} />
        </div>
        <div>
          <label htmlFor="vig-gratificacoes" className={ROTULO}>Gratificações e adicionais (R$)</label>
          <input id="vig-gratificacoes" inputMode="decimal" className={`${CAMPO} tabular`} aria-invalid={errors.gratificacoes ? true : undefined} {...form.register('gratificacoes')} />
          <p className="mt-1 text-caption text-ink-muted">Comissão, gratificação, periculosidade, insalubridade: entram nos encargos</p>
          <MensagemCampo id="vig-gratificacoes-erro" mensagem={errors.gratificacoes?.message} />
        </div>
        <div>
          <label htmlFor="vig-bonus" className={ROTULO}>Bônus e benefícios (R$)</label>
          <input id="vig-bonus" inputMode="decimal" className={`${CAMPO} tabular`} aria-invalid={errors.bonus ? true : undefined} {...form.register('bonus')} />
          <p className="mt-1 text-caption text-ink-muted">VR, saúde e outros benefícios: sem encargos</p>
          <MensagemCampo id="vig-bonus-erro" mensagem={errors.bonus?.message} />
        </div>
        <div>
          <label htmlFor="vig-horas" className={ROTULO}>Horas por dia</label>
          <input id="vig-horas" inputMode="decimal" className={`${CAMPO} tabular`} aria-invalid={errors.horasDia ? true : undefined} {...form.register('horasDia')} />
          <MensagemCampo id="vig-horas-erro" mensagem={errors.horasDia?.message} />
        </div>
        <div>
          <label htmlFor="vig-dias" className={ROTULO}>Dias por mês</label>
          <input id="vig-dias" inputMode="decimal" className={`${CAMPO} tabular`} aria-invalid={errors.diasMes ? true : undefined} {...form.register('diasMes')} />
          <MensagemCampo id="vig-dias-erro" mensagem={errors.diasMes?.message} />
        </div>
        <label htmlFor="vig-terceirizado" className="flex cursor-pointer items-center gap-2 self-end">
          <input id="vig-terceirizado" type="checkbox" className="size-[18px] accent-primary" {...form.register('terceirizado')} />
          Terceirizado
        </label>
        {terceirizado && (
          <div>
            <span className={ROTULO}>Fornecedor</span>
            <Controller
              control={form.control}
              name="fornecedorId"
              render={({ field }) => (
                <FornecedorCombobox
                  valor={fornecedor}
                  podeCadastrar={false}
                  ariaLabel="Fornecedor da vigência"
                  onSelecionar={(f) => {
                    setFornecedor(f);
                    field.onChange(f?.id ?? '');
                  }}
                />
              )}
            />
            <MensagemCampo id="vig-forn-erro" mensagem={errors.fornecedorId?.message} />
          </div>
        )}
        <div className="sm:col-span-2">
          <label htmlFor="vig-motivo" className={ROTULO}>Motivo (opcional)</label>
          <input id="vig-motivo" maxLength={300} className={CAMPO} {...form.register('motivo')} />
          <MensagemCampo id="vig-motivo-erro" mensagem={errors.motivo?.message} />
        </div>
      </fieldset>

      <div className="mt-5">
        <h4 className="mb-2 text-small font-semibold">Apropriação por centro de custo</h4>
        <div className="mt-3">
          <ApropriacaoPorCentro
            key={`${pessoa.id}-${chaveApropriacao}`}
            value={apropriacao?.valor ?? null}
            onChange={(valor, situacao) => setApropriacao({ valor, completa: situacao?.completa ?? valor !== null })}
            resolvida={pessoa.parametroVigente?.apropriacao ?? null}
            referTo="PESSOA"
            disabled={salvando}
            erro={erroApropriacao}
          />
        </div>
      </div>

      {erroSalvar && (
        <p role="alert" className="mt-4 text-caption text-danger-ink">
          {erroSalvar}
        </p>
      )}
      {salvoEm && !erroSalvar && (
        <p role="status" className="mt-4 text-caption text-ink-secondary">
          Vigência registrada às {salvoEm}.
        </p>
      )}
      <div className="mt-4 flex justify-end">
        <Button type="button" disabled={salvando} onClick={() => void form.handleSubmit(gravar, () => setErroSalvar('Corrija os campos destacados'))()}>
          {salvando ? 'Salvando…' : 'Salvar nova vigência'}
        </Button>
      </div>
    </section>
  );
}
